import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FileText, Search, Filter, Calendar, Share2, User, Eye, 
  Trash2, ExternalLink, ChevronLeft, ChevronRight, X,
  Link2, UserPlus, Clock, Download, SlidersHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document type options
const DOCUMENT_TYPES = [
  'Insurance Policy',
  'Training Brochure', 
  'Real Estate Document',
  'Legal Contract',
  'Financial Document',
  'Marketing Material',
  'Technical Documentation',
  'General Document'
];

// Document type badge colors
const DOC_TYPE_COLORS = {
  'Insurance Policy': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Training Brochure': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Real Estate Document': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Legal Contract': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial Document': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Marketing Material': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Technical Documentation': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'General Document': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const DocumentHistory = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sharedFilter, setSharedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (documentType && documentType !== 'all') params.append('document_type', documentType);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (sharedFilter === 'shared') params.append('is_shared', 'true');
      if (sharedFilter === 'private') params.append('is_shared', 'false');
      
      const res = await axios.get(`${BACKEND_URL}/api/documents/history/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDocuments(res.data.documents || []);
      setTotalDocs(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load document history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, searchQuery, documentType, dateFrom, dateTo, sharedFilter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDocuments();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDocumentType('all');
    setDateFrom('');
    setDateTo('');
    setSharedFilter('all');
    setCurrentPage(1);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const copyShareLink = async (doc) => {
    if (!doc.is_public || !doc.public_token) {
      toast.error('Document is not shared publicly');
      return;
    }
    
    const link = `${window.location.origin}/doc/${doc.public_token}`;
    await navigator.clipboard.writeText(link);
    toast.success('Public link copied to clipboard!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div data-testid="doc-history-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Document History</h1>
          <p className="text-emerald-300/70">View and manage all your analyzed documents</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30 bg-transparent"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate('/doc-analyzer')}
          >
            <FileText className="w-4 h-4 mr-2" />
            Analyze New
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300/70">Total Documents</p>
                <p className="text-2xl font-bold text-white">{totalDocs}</p>
              </div>
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300/70">Shared</p>
                <p className="text-2xl font-bold text-blue-400">
                  {documents.filter(d => d.is_public).length}
                </p>
              </div>
              <div className="p-2.5 bg-blue-500/20 rounded-lg">
                <Share2 className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300/70">Attached to Leads</p>
                <p className="text-2xl font-bold text-purple-400">
                  {documents.filter(d => d.attached_lead_id).length}
                </p>
              </div>
              <div className="p-2.5 bg-purple-500/20 rounded-lg">
                <UserPlus className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-300/70">This Page</p>
                <p className="text-2xl font-bold text-white">{documents.length}</p>
              </div>
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30 mb-6">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-emerald-500/50" />
              <Input
                data-testid="search-docs-input"
                placeholder="Search by document name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#0a3d2e] border-emerald-800/30 text-white placeholder:text-emerald-300/40 focus:border-emerald-500"
              />
            </div>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Search
            </Button>
          </form>

          {/* Expanded Filters */}
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-emerald-800/30"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Document Type */}
                <div>
                  <label className="block text-sm text-emerald-300/70 mb-1.5">Document Type</label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger data-testid="filter-doc-type" className="bg-[#0a3d2e] border-emerald-800/30 text-white">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {DOCUMENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm text-emerald-300/70 mb-1.5">Date From</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-[#0a3d2e] border-emerald-800/30 text-white"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm text-emerald-300/70 mb-1.5">Date To</label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="bg-[#0a3d2e] border-emerald-800/30 text-white"
                  />
                </div>

                {/* Shared Status */}
                <div>
                  <label className="block text-sm text-emerald-300/70 mb-1.5">Sharing Status</label>
                  <Select value={sharedFilter} onValueChange={setSharedFilter}>
                    <SelectTrigger data-testid="filter-shared" className="bg-[#0a3d2e] border-emerald-800/30 text-white">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="ghost"
                  className="text-emerald-400 hover:bg-emerald-900/30"
                  onClick={clearFilters}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card className="bg-[#052e22]/50 border-emerald-800/30">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-emerald-800 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No documents found</h3>
              <p className="text-emerald-300/70 mb-6">
                {searchQuery || documentType !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Start by analyzing your first document'}
              </p>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate('/doc-analyzer')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Analyze Document
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-emerald-800/30">
                    <th className="text-left p-4 text-sm font-medium text-emerald-300/70">Document</th>
                    <th className="text-left p-4 text-sm font-medium text-emerald-300/70">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-emerald-300/70">Upload Date</th>
                    <th className="text-left p-4 text-sm font-medium text-emerald-300/70">Shared</th>
                    <th className="text-left p-4 text-sm font-medium text-emerald-300/70">Attached Lead</th>
                    <th className="text-right p-4 text-sm font-medium text-emerald-300/70">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <motion.tr
                      key={doc.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b border-emerald-800/20 hover:bg-emerald-900/20 transition-colors"
                      data-testid={`doc-row-${index}`}
                    >
                      {/* Document Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate max-w-[200px]">
                              {doc.analysis?.title || doc.filename}
                            </p>
                            <p className="text-xs text-emerald-300/50">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="p-4">
                        <Badge className={DOC_TYPE_COLORS[doc.analysis?.document_type] || DOC_TYPE_COLORS['General Document']}>
                          {doc.analysis?.document_type || 'Unknown'}
                        </Badge>
                      </td>

                      {/* Date */}
                      <td className="p-4">
                        <span className="text-emerald-200 text-sm">
                          {formatDate(doc.created_at)}
                        </span>
                      </td>

                      {/* Shared Status */}
                      <td className="p-4">
                        {doc.is_public ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                              <Share2 className="w-3 h-3 mr-1" />
                              Shared
                            </Badge>
                            {doc.public_view_count > 0 && (
                              <span className="text-xs text-emerald-300/50 flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {doc.public_view_count}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-emerald-300/50 text-sm">Private</span>
                        )}
                      </td>

                      {/* Attached Lead */}
                      <td className="p-4">
                        {doc.attached_lead_name ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-purple-400" />
                            <span className="text-purple-300 text-sm">{doc.attached_lead_name}</span>
                          </div>
                        ) : (
                          <span className="text-emerald-300/50 text-sm">-</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-400 hover:bg-emerald-900/30"
                            onClick={() => navigate(`/doc-analyzer?view=${doc.id}`)}
                            title="View Analysis"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          {doc.is_public && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-400 hover:bg-blue-900/30"
                              onClick={() => copyShareLink(doc)}
                              title="Copy Public Link"
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-900/30"
                            onClick={() => handleDelete(doc.id)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-emerald-800/30">
              <p className="text-sm text-emerald-300/70">
                Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalDocs)} of {totalDocs}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-emerald-200 text-sm px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentHistory;
